import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AlertCircle, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // For now, just show success message
      // In the future, this would call a backend endpoint to send reset email
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call
      
      setEmailSent(true);
      toast.success('If an account exists with this email, you will receive password reset instructions.');
      
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center py-8 px-4">
      <div className="w-full max-w-md">
        {/* Back to Login Button */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/login')}
            className="flex items-center text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Button>
        </div>

        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_alberta-pharmacy-1/artifacts/euyis7mj_Logo%20-%20APS%20Icon%20-%20Large.png"
            alt="Alberta Pharmacy Supply Logo"
            className="w-24 h-24 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-slate-900">Reset Password</h1>
          <p className="text-slate-600 mt-2">We'll help you get back into your account</p>
        </div>
        
        <Card className="shadow-xl border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-center text-xl">Forgot Your Password?</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {!emailSent ? (
              <form onSubmit={handleSubmit} className="space-y-5">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    Enter your email address and we'll send you instructions to reset your password.
                  </AlertDescription>
                </Alert>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.pharmacy@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      disabled={isLoading}
                      autoFocus
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full font-semibold py-6"
                  style={{ background: 'linear-gradient(135deg, #7C3AED, #F97316)' }}
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send Reset Instructions'}
                </Button>
              </form>
            ) : (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Check Your Email</h3>
                <p className="text-slate-600 mb-6">
                  If an account exists with <strong>{email}</strong>, you will receive password reset instructions shortly.
                </p>
                <Button
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="w-full"
                >
                  Return to Login
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {!emailSent && (
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Need help?{' '}
              <a 
                href="mailto:aprxsupply@gmail.com" 
                className="text-purple-600 hover:text-purple-700 font-medium"
              >
                Contact Support
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
